import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/app/App';
/* i18n initialises on import and must complete before the first render, or every string flashes as
 * its key. The top-level `await` inside it makes this import block, which is why it is ordered
 * before the stylesheet and why the entry is a module. */
import '@/shared/i18n';
/* IBM Plex, **self-hosted**: these packages ship the woff2 files and are bundled by Vite, so the
 * faces are served from our own origin. Hard rule 13 is zero internet egress — the design mockups
 * load Plex from `fonts.googleapis.com`, which would make first paint depend on a network an
 * air-gapped bank does not have and would leak a request per visitor on one that does.
 *
 * Only the weights the design uses: 400/500/600 sans, 400/500 mono. Importing the package's index
 * would pull every weight and every subset — several megabytes of faces nothing renders. */
import '@fontsource/ibm-plex-sans/400.css';
import '@fontsource/ibm-plex-sans/500.css';
import '@fontsource/ibm-plex-sans/600.css';
import '@fontsource/ibm-plex-mono/400.css';
import '@fontsource/ibm-plex-mono/500.css';
import '@/shared/ui/tokens.css';
import '@/shared/ui/components.css';
/* After `components.css`: the revamp layer refines what that file established (a card's border
 * becomes an elevation), so load order is load-bearing rather than incidental. */
import '@/shared/ui/system.css';
import '@/shared/ui/shell.css';

const container = document.getElementById('root');
if (container === null) {
  /* index.html owns the mount point. Failing loudly beats rendering into a detached node and
   * spending an afternoon wondering why the page is blank. */
  throw new Error('#root is missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    {/* The router lives here rather than inside `App`, so a test can render `App` inside a
        MemoryRouter and drive navigation without a real history. */}
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
